import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {RunSetQuery} from '../../../../util/queryTypes';
import {BucketedQueryType} from './../queries/queryVars';

/*
This function selects one of six possible query types based on three orthogonal concerns:

1. **ID Type** (WithInternalId suffix):
   - Standard queries use projectId/entityId from the current workspace/report context
   - "Id" suffix queries use internalProjectId when the runset is from a different context (e.g., cross-project references)

2. **Grouping** (Grouped prefix):
   - Standard queries fetch individual runs
   - "Grouped" queries leverage backend aggregation when enabled, reducing frontend processing load
   - Grouped queries are only used when: backend grouping is enabled, querying a single runset,
     displaying grouped data, and not querying system metrics

3. **Line Construction** (BackendLines infix):
   - Standard queries use frontend line construction
   - "BackendLines" queries use backend-constructed line data for improved performance

Query Types:
- BucketedQuery (baseline)
- BucketedQueryId (baseline + internal ID)
- GroupedBucketedQuery (baseline + backend grouping)
- GroupedBucketedQueryId (baseline + backend grouping + internal ID)
- BucketedBackendLinesQuery (baseline + backend line construction)
- BucketedBackendLinesQueryId (baseline + backend line construction + internal ID)

See bucketed_data_queries.md for full architecture documentation.
*/
export const getQueryType = ({
  enableBackendGrouping,
  historyKeys,
  isGrouped,
  runSets,
  usesBackendLineConstruction,
  usesInternalId,
}: {
  enableBackendGrouping: boolean;
  historyKeys: string[];
  isGrouped: boolean;
  runSets: RunSetQuery[];
  usesBackendLineConstruction: boolean;
  usesInternalId: boolean;
}): BucketedQueryType => {
  const useGroupedQuery =
    enableBackendGrouping &&
    runSets.length < 2 &&
    isGrouped &&
    !historyKeys.some(key => isSystemMetric(key));

  if (useGroupedQuery) {
    return usesInternalId ? 'GroupedBucketedQueryId' : 'GroupedBucketedQuery';
  }

  if (usesBackendLineConstruction) {
    return usesInternalId
      ? 'BucketedBackendLinesQueryId'
      : 'BucketedBackendLinesQuery';
  }

  return usesInternalId ? 'BucketedQueryId' : 'BucketedQuery';
};
